import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api/client';

interface ExchangeRateData {
  usd_to_clp: number;
  last_updated: string;
  source: string;
  from_cache?: boolean;
  warning?: string;
}

// Default fallback rate
const DEFAULT_RATE = 900;

/**
 * Hook to fetch and manage USD/CLP exchange rate
 */
export function useExchangeRate() {
  return useQuery<ExchangeRateData>({
    queryKey: ['exchangeRate'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/exchange-rate');
        return response.data.data;
      } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
        // Return default rate on error
        return {
          usd_to_clp: DEFAULT_RATE,
          last_updated: new Date().toISOString(),
          source: 'default',
          warning: 'Using default rate due to API error'
        };
      }
    },
    staleTime: 60 * 60 * 1000, // Consider data fresh for 1 hour
    cacheTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    retry: 1,
    initialData: {
      usd_to_clp: DEFAULT_RATE,
      last_updated: new Date().toISOString(),
      source: 'default',
      warning: 'Using default rate'
    }
  });
}

/**
 * Hook to set manual exchange rate
 */
export function useSetExchangeRate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (rate: number) => {
      const response = await apiClient.post('/exchange-rate', {
        usd_to_clp: rate
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Update the cache with new rate
      queryClient.setQueryData(['exchangeRate'], data.data);
      queryClient.invalidateQueries({ queryKey: ['exchangeRate'] });
    }
  });
}

/**
 * Utility functions for currency conversion
 */
export const currencyUtils = {
  /**
   * Convert CLP to USD
   */
  clpToUsd: (amountClp: number, rate: number = DEFAULT_RATE): number => {
    return amountClp / rate;
  },
  
  /**
   * Convert USD to CLP
   */
  usdToClp: (amountUsd: number, rate: number = DEFAULT_RATE): number => {
    return amountUsd * rate;
  },
  
  /**
   * Convert CLP/kWh to USD/MWh
   */
  clpKwhToUsdMwh: (clpKwh: number, rate: number = DEFAULT_RATE): number => {
    return (clpKwh / rate) * 1000;
  },
  
  /**
   * Convert USD/MWh to CLP/kWh
   */
  usdMwhToClpKwh: (usdMwh: number, rate: number = DEFAULT_RATE): number => {
    return (usdMwh * rate) / 1000;
  },
  
  /**
   * Format currency display
   */
  formatClp: (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  },
  
  formatUsd: (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
};

/**
 * Get exchange rate from cache or use default
 */
export function getCachedExchangeRate(): number {
  try {
    const cached = localStorage.getItem('exchangeRate');
    if (cached) {
      const data = JSON.parse(cached);
      // Check if cache is less than 1 hour old
      if (Date.now() - data.timestamp < 3600000) {
        return data.rate;
      }
    }
  } catch (error) {
    console.error('Error reading cached exchange rate:', error);
  }
  return DEFAULT_RATE;
}

/**
 * Cache exchange rate locally
 */
export function cacheExchangeRate(rate: number): void {
  try {
    localStorage.setItem('exchangeRate', JSON.stringify({
      rate,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error caching exchange rate:', error);
  }
}